import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Star, TrendingUp, Users, Award, Plus } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCountry } from '../context/CountryContext';
import { AuthModal } from '../components/auth/AuthModal';
import { CardCustomizationModal } from '../components/shop/CardCustomizationModal';
import { apiRequest } from '../utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function ReviewCardsPage() {
  const navigate = useNavigate();
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showCustomizationModal, setShowCustomizationModal] = useState(false);
  const [selectedCardType, setSelectedCardType] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [reviewCards, setReviewCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isAuthenticated } = useAuth();
  const { formatPrice } = useCountry();

  useEffect(() => {
    fetchReviewCards();
  }, []);

  const fetchReviewCards = async () => {
    try {
      const response = await apiRequest(`${BACKEND_URL}/api/admin/public/review-standees`, {
        method: 'GET',
      });

      if (response.ok) {
        setReviewCards(response.data.review_standees || []);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching review standees:', err);
      setLoading(false);
    }
  };

  const handleBuyNow = (card) => {
    setSelectedCard(card);
    setSelectedCardType(card.name);
    if (isAuthenticated) {
      setShowCustomizationModal(true);
    } else {
      setShowAuthModal(true);
    }
  };

  const handleAuthSuccess = () => {
    setShowAuthModal(false);
    setShowCustomizationModal(true);
  };

  const handleCustomizationConfirm = (customData) => {
    console.log('Card customization data:', customData);
    console.log('Card type:', selectedCardType);
    
    // Navigate to checkout page with order data including full card details
    setShowCustomizationModal(false);
    navigate('/checkout', {
      state: {
        card: selectedCard, // Pass full card object with materials and pricing
        cardType: selectedCardType,
        displayName: customData.displayName,
        logoPreview: customData.logoPreview,
      }
    });
  };

  const faqs = [
    {
      question: 'How do review standees work?',
      answer: 'Review standees use NFC technology to instantly direct customers to your review page. Simply tap the standee with any smartphone, and they can leave a review in seconds.',
    },
    {
      question: 'Which platforms are supported?',
      answer: 'We currently support Google Reviews and TripAdvisor, with more platforms coming soon.',
    },
    {
      question: 'Can I customize my review standee?',
      answer: 'Yes! You can customize the design, colors, and branding to match your business identity.',
    },
    {
      question: 'How long does delivery take?',
      answer: 'Standard delivery takes 5-7 business days. Express shipping options are available.',
    },
    {
      question: 'Do customers need an app to use review standees?',
      answer: 'No app required! Review standees work with any NFC-enabled smartphone, which includes most modern devices.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl lg:text-6xl font-extrabold text-navy mb-6">
            Review Standees
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Transform customer feedback into your most powerful marketing tool
          </p>
        </div>
      </section>

      {/* Review Standee Types Section (Listing) */}
      <section className="py-16 px-4 bg-gradient-to-br from-[#9B7EFF] to-[#7D65FA]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-5xl font-extrabold text-navy mb-4">
              Choose Your Review Standee
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Select the platform that matters most to your business
            </p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy mx-auto"></div>
              <p className="text-gray-600 mt-4">Loading standees...</p>
            </div>
          ) : reviewCards.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600">No review standees available at the moment.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
              {reviewCards.map((card) => (
                <div key={card.id} className="bg-dark-gray rounded-[2.5rem] p-8 text-white relative overflow-hidden flex flex-col">
                  {/* Sample Design Image */}
                  {card.sample_design_url && (
                    <div className="mb-6 -mx-8 -mt-8">
                      <img
                        src={card.sample_design_url}
                        alt={card.name}
                        className="w-full h-48 object-cover"
                      />
                    </div>
                  )}

                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-4">
                      {card.name}
                    </h3>
                    
                    {/* Short Description */}
                    {card.short_description && (
                      <p className="text-gray-300 mb-6">
                        {card.short_description}
                      </p>
                    )}
                    
                    {/* Features */}
                    {card.features && card.features.length > 0 && (
                      <ul className="space-y-2 mb-6">
                        {card.features.map((feature, index) => (
                          <li key={index} className="flex items-start gap-2">
                            <Star className="w-4 h-4 fill-yellow-400 text-yellow-400 flex-shrink-0 mt-1" />
                            <span className="text-sm">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* Materials & Pricing */}
                    {card.materials && card.materials.length > 0 && (
                      <div className="mb-6">
                        <p className="text-xs text-gray-400 mb-2">Available Materials:</p>
                        <div className="space-y-1">
                          {card.materials.map((material) => (
                            <div key={material.id} className="flex justify-between text-sm">
                              <span>{material.name}</span>
                              <span className="font-semibold">{formatPrice(material.price)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Buy Button */}
                  <Button 
                    onClick={() => handleBuyNow(card)}
                    className="bg-white text-navy hover:bg-gray-100 rounded-full px-8 py-4 text-base font-semibold w-full mt-auto"
                  >
                    Buy Now
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* What are Review Standees Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Text */}
            <div>
              <h2 className="text-4xl font-extrabold text-navy mb-6">
                What are Review Standees?
              </h2>
              <div className="space-y-4 text-lg text-gray-700">
                <p>
                  Review Standees are NFC-enabled smart standees that make it incredibly easy for 
                  your customers to leave reviews. With a single tap, customers are directed 
                  to your review page on platforms like Google or TripAdvisor.
                </p>
                <p>
                  No apps, no typing, no hassle - just tap and review. It's the fastest way 
                  to collect authentic customer feedback and build your online reputation.
                </p>
              </div>
              <div className="grid grid-cols-3 gap-6 mt-8">
                <div className="text-center">
                  <div className="text-3xl font-bold text-navy mb-2">5x</div>
                  <div className="text-sm text-gray-600">More Reviews</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-navy mb-2">2 Sec</div>
                  <div className="text-sm text-gray-600">To Review</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-navy mb-2">100%</div>
                  <div className="text-sm text-gray-600">Authentic</div>
                </div>
              </div>
            </div>

            {/* Right - Image */}
            <div className="relative">
              <div className="bg-purple-custom rounded-[2.5rem] p-12 min-h-[500px] flex items-center justify-center">
                <img
                  src="https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=600&fit=crop"
                  alt="Review Cards in Action"
                  className="rounded-2xl shadow-lg w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What's in it for your business Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Image */}
            <div className="relative order-2 lg:order-1">
              <div className="bg-yellow-custom rounded-[2.5rem] p-12 min-h-[500px] flex items-center justify-center">
                <img
                  src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop"
                  alt="Business Growth"
                  className="rounded-2xl shadow-lg w-full h-auto"
                />
              </div>
            </div>

            {/* Right - Text */}
            <div className="order-1 lg:order-2">
              <h2 className="text-4xl font-extrabold text-navy mb-6">
                What's in it for your business?
              </h2>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-purple-custom rounded-xl flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-6 h-6 text-navy" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-navy mb-2">
                      Boost Your Online Reputation
                    </h3>
                    <p className="text-gray-600">
                      More reviews mean better rankings on Google and TripAdvisor. Stand out 
                      from competitors and attract more customers.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Users className="w-6 h-6 text-navy" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-navy mb-2">
                      Build Customer Trust
                    </h3>
                    <p className="text-gray-600">
                      Authentic reviews from real customers create social proof that converts 
                      prospects into paying customers.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Award className="w-6 h-6 text-navy" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-navy mb-2">
                      Increase Revenue
                    </h3>
                    <p className="text-gray-600">
                      Businesses with more positive reviews see an average 31% increase in 
                      revenue. Let your customers become your best marketers.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-5xl font-extrabold text-navy mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600">
              Everything you need to know about Review Standees
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {faqs.map((faq, index) => (
              <div key={index} className="border-b border-gray-200 pb-4">
                <button
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  className="w-full flex items-center justify-between text-left py-4"
                >
                  <span className="text-lg font-semibold text-navy pr-4">
                    {faq.question}
                  </span>
                  <Plus 
                    className={`w-6 h-6 text-navy flex-shrink-0 transition-transform ${
                      openFaqIndex === index ? 'rotate-45' : ''
                    }`}
                  />
                </button>
                {openFaqIndex === index && (
                  <p className="text-gray-600 pb-4">
                    {faq.answer}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleAuthSuccess}
      />

      {/* Card Customization Modal */}
      <CardCustomizationModal
        isOpen={showCustomizationModal}
        onClose={() => setShowCustomizationModal(false)}
        onConfirm={handleCustomizationConfirm}
        cardType={selectedCardType}
        productImage={selectedCard?.sample_design_url}
      />
    </div>
  );
}
